import { useState, useRef, useEffect } from "react";
import { 
  Upload, 
  FileText, 
  MessageSquare, 
  Send, 
  Loader2, 
  TrendingUp, 
  TrendingDown,
  PieChart,
  AlertCircle,
  CheckCircle,
  Bot,
  User,
  FileSpreadsheet,
  Image,
  X,
  RefreshCw,
  AlertTriangle,
  ArrowRight,
  Scissors,
  Plus,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import API from "../config/api";

const SECTOR_COLORS = {
  'Technology': '#3b82f6',
  'Healthcare': '#10b981',
  'Financial Services': '#f59e0b',
  'Consumer Cyclical': '#ec4899',
  'Communication Services': '#8b5cf6',
  'Industrials': '#6366f1',
  'Consumer Defensive': '#14b8a6',
  'Energy': '#f97316',
  'Utilities': '#06b6d4',
  'Real Estate': '#84cc16',
  'Basic Materials': '#a855f7',
  'Unknown': '#6b7280',
  'N/A': '#9ca3af'
};

const getColorForSector = (sector, index) => {
  return SECTOR_COLORS[sector] || Object.values(SECTOR_COLORS)[index % Object.values(SECTOR_COLORS).length];
};

const PortfolioAnalyzerPage = () => {
  const [sessionId, setSessionId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [sectorAllocation, setSectorAllocation] = useState({});
  const [subSectorAllocation, setSubSectorAllocation] = useState({});
  const [rebalancingActions, setRebalancingActions] = useState([]);
  const [identifiedLosers, setIdentifiedLosers] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('holdings'); // 'holdings', 'sectors', 'rebalance'
  
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['.pdf', '.xlsx', '.xls', '.jpg', '.jpeg', '.png'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      setError("Please upload a PDF, Excel, or Image file (.pdf, .xlsx, .xls, .jpg, .jpeg, .png)");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be under 10MB");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/portfolio-analyzer/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();
      
      setSessionId(data.session_id);
      setUploadedFile({
        name: file.name,
        type: fileExt,
        tickersFound: data.tickers_found,
        holdingsParsed: data.holdings_parsed
      });
      setHoldings(data.holdings || []);
      setAnalysis(data.initial_analysis);
      setSectorAllocation(data.initial_analysis?.sector_allocation || {});
      setSubSectorAllocation(data.initial_analysis?.sub_sector_allocation || {});
      setRebalancingActions(data.initial_analysis?.rebalancing_actions || []);
      setIdentifiedLosers(data.initial_analysis?.identified_losers || []);
      
      if (data.initial_analysis?.summary) {
        setChatMessages([{
          role: 'assistant',
          content: data.initial_analysis.summary,
          timestamp: new Date().toISOString()
        }]);
      }

    } catch (err) {
      setError(err.message || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !sessionId || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatLoading(true);

    setChatMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const response = await fetch(`${API}/portfolio-analyzer/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage
        })
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      const data = await response.json();
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      }]);

    } catch (err) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const resetAnalyzer = () => {
    setSessionId(null);
    setUploadedFile(null);
    setHoldings([]);
    setAnalysis(null);
    setSectorAllocation({});
    setSubSectorAllocation({});
    setRebalancingActions([]);
    setIdentifiedLosers([]);
    setChatMessages([]);
    setChatInput("");
    setError(null);
    setActiveTab('holdings');
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getChangeColor = (val) => {
    if (val > 0) return "text-emerald-600";
    if (val < 0) return "text-red-600";
    return "text-slate-600";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'LOW': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'SELL': return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'REDUCE': return <Scissors className="w-4 h-4 text-orange-500" />;
      case 'DIVERSIFY': return <PieChart className="w-4 h-4 text-blue-500" />;
      case 'ADD': return <Plus className="w-4 h-4 text-green-500" />;
      default: return <ArrowRight className="w-4 h-4" />;
    }
  };

  // Prepare pie chart data
  const pieChartData = Object.entries(sectorAllocation).map(([name, data], index) => ({
    name,
    value: data.pct,
    amount: data.value,
    holdings: data.holdings,
    fill: getColorForSector(name, index)
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-slate-900">{data.name}</p>
          <p className="text-sm text-slate-600">{data.value.toFixed(1)}% allocation</p>
          <p className="text-sm text-slate-600">${data.amount?.toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">{data.holdings?.length} holdings</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
              <PieChart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Portfolio Analyzer
              </h1>
              <p className="text-sm text-slate-500">AI-Powered Portfolio Analysis & Rebalancing</p>
            </div>
          </div>
          
          {sessionId && (
            <Button 
              onClick={resetAnalyzer}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              New Analysis
            </Button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Upload Section */}
        {!sessionId && (
          <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50 hover:border-blue-400 transition-colors">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-upload-input"
              />
              
              {uploading ? (
                <div className="space-y-3 w-full max-w-md mx-auto">
                  <div className="animate-pulse space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      {[1,2,3].map(i => (
                        <div key={i} className="bg-slate-100 rounded-lg p-3">
                          <div className="bg-slate-200 rounded h-3 w-1/2 mb-2" />
                          <div className="bg-slate-200 rounded h-5 w-2/3" />
                        </div>
                      ))}
                    </div>
                    <div className="bg-slate-100 rounded-lg p-4 h-48">
                      <div className="bg-slate-200 rounded h-3 w-1/4 mb-3" />
                      <div className="flex items-end gap-1 h-[80%]">
                        {Array.from({length:12}).map((_,i) => (
                          <div key={i} className="bg-slate-200 rounded-t flex-1" style={{height: `${20+Math.random()*60}%`}} />
                        ))}
                      </div>
                    </div>
                    {Array.from({length:4}).map((_,r) => (
                      <div key={r} className="flex gap-4">
                        {[1,2,3,4].map(c => <div key={c} className="bg-slate-200 rounded h-3 flex-1" />)}
                      </div>
                    ))}
                  </div>
                  <p className="text-lg font-medium text-slate-700 text-center mt-4">Analyzing your portfolio...</p>
                  <p className="text-sm text-slate-500 text-center">Parsing file, fetching sector data, and identifying rebalancing opportunities</p>
                </div>
              ) : (
                <>
                  <div className="flex gap-4 mb-6">
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <FileText className="w-10 h-10 text-red-500" />
                    </div>
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <FileSpreadsheet className="w-10 h-10 text-green-500" />
                    </div>
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <Image className="w-10 h-10 text-blue-500" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    Upload Your Portfolio
                  </h3>
                  <p className="text-slate-600 mb-6 text-center max-w-md">
                    Upload a PDF or Excel file containing your portfolio holdings.
                    The AI will analyze sectors, identify losers, and suggest rebalancing.
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    data-testid="upload-portfolio-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select File
                  </Button>
                  <p className="text-xs text-slate-500 mt-4">
                    Supported: PDF, Excel (.xlsx, .xls), Images (.jpg, .jpeg, .png) • Max 10MB
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Analysis Dashboard */}
        {sessionId && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Holdings & Analysis */}
            <div className="lg:col-span-2 space-y-6">
              {/* File Info */}
              <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {uploadedFile?.type === '.pdf' ? (
                        <FileText className="w-8 h-8" />
                      ) : (
                        <FileSpreadsheet className="w-8 h-8" />
                      )}
                      <div>
                        <p className="font-semibold">{uploadedFile?.name}</p>
                        <p className="text-sm text-blue-100">
                          {uploadedFile?.tickersFound} tickers found • {holdings.length} with market data
                        </p>
                      </div>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-300" />
                  </div>
                </CardContent>
              </Card>

              {/* Portfolio Metrics */}
              {analysis?.metrics && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-white">
                    <CardContent className="py-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">
                        ${(analysis.metrics.total_value || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                      <p className="text-xs text-slate-500">Est. Total Value</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white">
                    <CardContent className="py-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{analysis.metrics.num_holdings}</p>
                      <p className="text-xs text-slate-500">Holdings</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white">
                    <CardContent className="py-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{analysis.metrics.num_sectors || Object.keys(sectorAllocation).length}</p>
                      <p className="text-xs text-slate-500">Sectors</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white">
                    <CardContent className="py-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{analysis.metrics.gainers}</p>
                      <p className="text-xs text-slate-500">Gainers</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white">
                    <CardContent className="py-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{analysis.metrics.losers}</p>
                      <p className="text-xs text-slate-500">Losers</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-2 border-b border-slate-200 pb-2">
                <button
                  onClick={() => setActiveTab('holdings')}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                    activeTab === 'holdings' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-holdings"
                >
                  Holdings
                </button>
                <button
                  onClick={() => setActiveTab('sectors')}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'sectors' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-sectors"
                >
                  <PieChart className="w-4 h-4" />
                  Sector Allocation
                </button>
                <button
                  onClick={() => setActiveTab('rebalance')}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'rebalance' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-rebalance"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Rebalance
                  {rebalancingActions.length > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {rebalancingActions.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Holdings Table */}
              {activeTab === 'holdings' && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                      Portfolio Holdings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Ticker</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Company</th>
                            <th className="text-right py-2 px-3 font-medium text-slate-600">Price</th>
                            <th className="text-right py-2 px-3 font-medium text-slate-600">Change</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Sector</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Sub-Sector</th>
                          </tr>
                        </thead>
                        <tbody>
                          {holdings.map((holding, idx) => (
                            <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50 ${holding.isLoser ? 'bg-red-50' : ''}`}>
                              <td className="py-2 px-3 font-semibold text-slate-900">
                                {holding.ticker}
                                {holding.isLoser && <AlertTriangle className="inline w-3 h-3 text-red-500 ml-1" />}
                              </td>
                              <td className="py-2 px-3 text-slate-600 truncate max-w-[150px]">
                                {holding.companyName}
                              </td>
                              <td className="py-2 px-3 text-right font-mono">
                                ${holding.currentPrice?.toFixed(2)}
                              </td>
                              <td className={`py-2 px-3 text-right font-medium ${getChangeColor(holding.changePercent)}`}>
                                <span className="flex items-center justify-end gap-1">
                                  {holding.changePercent > 0 ? (
                                    <TrendingUp className="w-3 h-3" />
                                  ) : holding.changePercent < 0 ? (
                                    <TrendingDown className="w-3 h-3" />
                                  ) : null}
                                  {holding.changePercent?.toFixed(2)}%
                                </span>
                              </td>
                              <td className="py-2 px-3 text-slate-600 text-xs">{holding.sector}</td>
                              <td className="py-2 px-3 text-slate-500 text-xs">{holding.subSector || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {holdings.length === 0 && (
                      <p className="text-center py-8 text-slate-500">
                        No holdings with market data found
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Sector Allocation Tab */}
              {activeTab === 'sectors' && (
                <div className="space-y-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-blue-600" />
                        Sector Allocation
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Pie Chart */}
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                              <Pie
                                data={pieChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, value }) => `${name.substring(0, 10)}... ${value.toFixed(0)}%`}
                                labelLine={false}
                              >
                                {pieChartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip content={<CustomTooltip />} />
                            </RechartsPie>
                          </ResponsiveContainer>
                        </div>
                        
                        {/* Sector List */}
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {Object.entries(sectorAllocation).map(([sector, data], index) => (
                            <div key={sector} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-4 h-4 rounded" 
                                  style={{ backgroundColor: getColorForSector(sector, index) }}
                                />
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">{sector}</p>
                                  <p className="text-xs text-slate-500">{data.holdings?.length} holdings</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-slate-900">{data.pct?.toFixed(1)}%</p>
                                <p className="text-xs text-slate-500">${data.value?.toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Sub-Sector Breakdown */}
                  {Object.keys(subSectorAllocation).length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-indigo-600" />
                          Sub-Sector Breakdown
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.entries(subSectorAllocation).slice(0, 10).map(([subSector, data], index) => (
                            <div key={subSector} className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-slate-700">{subSector}</span>
                                  <span className="font-medium">{data.pct?.toFixed(1)}%</span>
                                </div>
                                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all"
                                    style={{ 
                                      width: `${Math.min(data.pct, 100)}%`,
                                      backgroundColor: Object.values(SECTOR_COLORS)[index % Object.values(SECTOR_COLORS).length]
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Rebalancing Tab */}
              {activeTab === 'rebalance' && (
                <div className="space-y-6">
                  {/* Rebalancing Actions */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        Rebalancing Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {rebalancingActions.length > 0 ? (
                        <div className="space-y-3">
                          {rebalancingActions.map((action, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border ${getPriorityColor(action.priority)}`}
                            >
                              <div className="flex items-start gap-3">
                                {getActionIcon(action.action)}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-sm uppercase">{action.action}</span>
                                    {action.ticker && (
                                      <span className="font-mono font-semibold">{action.ticker}</span>
                                    )}
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      action.priority === 'HIGH' ? 'bg-red-200 text-red-800' :
                                      action.priority === 'MEDIUM' ? 'bg-yellow-200 text-yellow-800' :
                                      'bg-blue-200 text-blue-800'
                                    }`}>
                                      {action.priority}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-600 mb-1">{action.reason}</p>
                                  <p className="text-sm font-medium">{action.suggestion}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                          <p className="text-slate-600">No immediate rebalancing actions needed!</p>
                          <p className="text-sm text-slate-500">Your portfolio appears well-balanced.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Identified Losers */}
                  {identifiedLosers.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader className="pb-2 bg-red-50">
                        <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                          <TrendingDown className="w-5 h-5" />
                          Losers to Consider Cutting
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-red-200">
                                <th className="text-left py-2 px-3 font-medium text-red-700">Ticker</th>
                                <th className="text-left py-2 px-3 font-medium text-red-700">Sector</th>
                                <th className="text-right py-2 px-3 font-medium text-red-700">Today</th>
                                <th className="text-right py-2 px-3 font-medium text-red-700">Total G/L</th>
                                <th className="text-left py-2 px-3 font-medium text-red-700">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {identifiedLosers.map((loser, idx) => (
                                <tr key={idx} className="border-b border-red-100 hover:bg-red-50">
                                  <td className="py-2 px-3 font-semibold text-slate-900">{loser.ticker}</td>
                                  <td className="py-2 px-3 text-slate-600">{loser.sector || 'N/A'}</td>
                                  <td className="py-2 px-3 text-right text-red-600 font-medium">
                                    {loser.changePercent?.toFixed(2)}%
                                  </td>
                                  <td className="py-2 px-3 text-right text-red-600 font-medium">
                                    {loser.gainLossPct?.toFixed(2)}%
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium">
                                      {loser.gainLossPct < -15 ? 'SELL' : 'REDUCE'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>

            {/* Right Column - AI Chat */}
            <div className="lg:col-span-1">
              <Card className="h-[700px] flex flex-col">
                <CardHeader className="pb-2 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bot className="w-5 h-5 text-indigo-600" />
                    AI Assistant
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col p-0">
                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {chatMessages.length === 0 && (
                      <div className="text-center py-8">
                        <Bot className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm">
                          Ask me about your portfolio, sectors, or rebalancing strategies.
                        </p>
                      </div>
                    )}
                    
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                            <Bot className="w-4 h-4 text-indigo-600" />
                          </div>
                        )}
                        <div
                          className={`max-w-[85%] rounded-lg px-4 py-2 text-sm ${
                            msg.role === 'user'
                              ? 'bg-indigo-600 text-white'
                              : msg.error
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {chatLoading && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <Bot className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="bg-slate-100 rounded-lg px-4 py-3">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                        </div>
                      </div>
                    )}
                    
                    <div ref={chatEndRef} />
                  </div>

                  {/* Chat Input */}
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                        placeholder="Ask about your portfolio..."
                        className="flex-1"
                        disabled={chatLoading}
                        data-testid="chat-input"
                      />
                      <Button
                        onClick={sendChatMessage}
                        disabled={!chatInput.trim() || chatLoading}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="send-chat-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {['Show sector risks', 'Suggest rebalancing', 'Cut losers strategy', 'Diversification tips'].map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => setChatInput(suggestion)}
                          className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PortfolioAnalyzerPage;
